import {
  AccountingConnectionsResponse,
  AccountingRedirectPayload,
  AttioConnection,
  AttioDealStage,
  AuditLog,
  CrmConnection,
  CrmHealthCheck,
  CrmResyncResponse,
  CrmType,
  CustomFieldEntityEnum,
  DocumentDefinition,
  ESignConfig,
  ExternalSystem,
  HubSpotSyncBehavior,
  IApiKey,
  IBillPricing,
  ICurrencyResp,
  ICurrencySchema,
  IDealRoomSettingSchema,
  IDunningCreateSchema,
  IDunnings,
  IHistory,
  IInvoiceSettings,
  INotificationItemSchema,
  INotificationSchema,
  IntegrationLogEventPublishReq,
  IntegrationLogEventPublishResponse,
  IntegrationLogResponse,
  IPaymentCollectAutoConfigReqSchema,
  IPaymentCollectAutoConfigResSchema,
  IPaymentGateway,
  IQuoteApprovalMessageSchema,
  IQuoteSettingsConfigSchema,
  IQuoteTemplateConfigRes,
  ISlackChannelResponse,
  ISlackConnectionResponse,
  ITaxConnectionVerifyReqSchema,
  ITeam,
  ITeamOwnerUpdateSchema,
  ITeamResponseSchema,
  ITeamUpdateSchema,
  QuoteDocumentType,
  QuoteDocumentTypeRequest,
  QuoteExpirationReminderSchema,
  QuoteStatusToDealStageMappingType,
  TIntegrationLogRequestSchema,
} from '@monetize/types/app';
import {
  ApiListInfiniteScrollResponse,
  ApiListResponse,
  DEFAULT_PAGER,
  GenericApiResponse,
  GetListApiConfig,
  GetListApiFilter,
} from '@monetize/types/common';
import {
  UndefinedInitialDataInfiniteOptions,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { logger } from '../services/logger';
import { ReferenceResource } from '../types';
import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
  apiUpload,
  apiUploadPut,
} from './axios';
import { associationsQueryKeys } from './associationsService';
import { ApiQueryItem } from './queryUtils';
import {
  updateListCacheWithAddedItem,
  updateListCacheWithRemovedItem,
  updateListCacheWithUpdatedItem,
} from './queryUtilsHelpers';
import { asQueryUtil, composeGetQuery } from './utils';

export const settingsQueryKeys = {
  base: ['settings'] as const,
  quoteExpirationReminder: () =>
    [...settingsQueryKeys.base, 'quoteExpirationReminders'] as const,
  companySetting: () => [...settingsQueryKeys.base, 'companySetting'] as const,
  companyLogo: () => [...settingsQueryKeys.base, 'companyLogo'] as const,
  customContract: () => [...settingsQueryKeys.base, 'custom-contract'] as const,
  quoteTemplate: () => [...settingsQueryKeys.base, 'quote-template'] as const,
  quoteSettings: () => [...settingsQueryKeys.base, 'quote-settings'] as const,
  quoteDocumentTypes: () =>
    [...settingsQueryKeys.base, 'quote-document-types'] as const,

  crm: () => [...settingsQueryKeys.base, 'crm'] as const,
  crmSalesforce: () => [...settingsQueryKeys.crm(), 'salesforce'] as const,
  // Not nested under crm(): each check makes a live call to the CRM, so it
  // shouldn't re-run whenever the connection config is invalidated.
  crmHealthCheck: () => [...settingsQueryKeys.base, 'crmHealthCheck'] as const,

  currency: () => [...settingsQueryKeys.base, 'currency'] as const,
  currencyList: () => [...settingsQueryKeys.currency(), 'list'] as const,

  dunning: () => [...settingsQueryKeys.base, 'dunning'] as const,
  dunningList: () => [...settingsQueryKeys.dunning(), 'list'] as const,
  dunningDetail: (id: string) => [...settingsQueryKeys.dunning(), id] as const,

  team: () => [...settingsQueryKeys.base, 'team'] as const,
  teamList: () => [...settingsQueryKeys.team(), 'list'] as const,
  teamDetail: (teamId: string) =>
    [...settingsQueryKeys.team(), teamId] as const,
  teamHistory: (teamId: string) =>
    [...settingsQueryKeys.team(), 'history', teamId] as const,
  pricing: () => [...settingsQueryKeys.base, 'pricing'] as const,
  apiKeyList: () => [...settingsQueryKeys.base, 'apiKeyList'] as const,
  salesQuoteApprovalMessage: () =>
    [...settingsQueryKeys.base, 'salesQuoteApprovalMessage'] as const,
  invoiceSettings: () =>
    [...settingsQueryKeys.base, 'invoiceSettings'] as const,
  earliestManualPaymentDateSettings: () =>
    [...settingsQueryKeys.base, 'earliestManualPaymentDateSettings'] as const,

  // eSignContacts
  esignConfig: () => [...settingsQueryKeys.base, 'esignConfig'] as const,
  eSignContact: () => [...settingsQueryKeys.base, 'eSignContact'] as const,
  eSignContactList: () =>
    [...settingsQueryKeys.eSignContact(), 'list'] as const,
  billGroup: () => [...settingsQueryKeys.base, 'billGroup'] as const,
  billGroupConfig: () => [...settingsQueryKeys.billGroup(), 'config'] as const,

  // Notifications
  notifications: () => [...settingsQueryKeys.base, 'notifications'] as const,

  // Accounting
  accounting: () => [...settingsQueryKeys.base, 'accounting'] as const,

  // Integration Log
  integrationLog: () => [...settingsQueryKeys.base, 'integrationLog'] as const,
  integrationLogDetail: (id: string) =>
    [...settingsQueryKeys.integrationLog(), id] as const,

  // Slack
  slack: () => [...settingsQueryKeys.base, 'slack'] as const,

  // Invoices
  invoiceTemplate: () =>
    [...settingsQueryKeys.base, 'invoice-template'] as const,

  // CustomFields
  customFieldsList: () =>
    [...settingsQueryKeys.base, 'customFieldsList'] as const,
  customFieldsById: (entity: string, key: string) =>
    [...settingsQueryKeys.customFieldsList(), entity, key] as const,

  customFieldsListByEntity: (
    entity: CustomFieldEntityEnum | Array<CustomFieldEntityEnum>,
  ) => [...settingsQueryKeys.base, 'customFieldsList', entity] as const,

  tax: () => [...settingsQueryKeys.base, 'tax'] as const,
  taxByProvider: (provider: string) =>
    [...settingsQueryKeys.tax(), provider] as const,
  dealroomConfig: () => [...settingsQueryKeys.base, 'dealroomConfig'],
  referenceResources: () => [...settingsQueryKeys.base, 'referenceResources'],
  referenceResourceById: (referenceId: string) => [
    ...settingsQueryKeys.base,
    'referenceResourceById',
    referenceId,
  ],

  quoteDocumentDefinitions: () =>
    [...settingsQueryKeys.base, 'quoteDocumentDefinitions'] as const,

  // Audit Log
  auditLog: () => [...settingsQueryKeys.base, 'auditLog'] as const,
};

const customFieldQueryKeys: ApiQueryItem = {
  byId: {
    endpoint: (id: string, args) =>
      `/api/configurations/customFields/${args.entity}/${args.key}`,
    queryKey: (id: string, args) =>
      settingsQueryKeys.customFieldsById(args.entity, args.key),
  },
  update: {
    endpoint: (id: string, args) =>
      `/api/configurations/customFields/${args.entity}/${args.key}`,
    invalidateKeys: [settingsQueryKeys.customFieldsList()],
    setDataKey: (id: string, args) =>
      settingsQueryKeys.customFieldsById(args.entity, args.key),
  },
  create: {
    endpoint: () => `/api/configurations/customFields`,
    invalidateKeys: [settingsQueryKeys.customFieldsList()],
    setDataKey: (id: string, args) =>
      settingsQueryKeys.customFieldsById(args.entity, args.key),
  },
  list: {
    endpoint: `/api/configurations/customFields`,
    queryKey: settingsQueryKeys.customFieldsList(),
  },
};

const taxQueryKeys: ApiQueryItem = {
  list: {
    endpoint: (args) => `/api/configurations/integrations/taxes`,
    queryKey: settingsQueryKeys.tax(),
  },
  create: {
    endpoint: () => `/api/configurations/integrations/taxes`,
    invalidateKeys: [settingsQueryKeys.tax()],
    setDataKey: (id: string, args) => settingsQueryKeys.taxByProvider(id),
  },
  byId: {
    endpoint: (id: string) => `/api/configurations/integrations/taxes/${id}`,
    queryKey: (id: string) => settingsQueryKeys.taxByProvider(id),
  },
  update: {
    endpoint: (id) => `/api/configurations/integrations/taxes/${id}`,
    invalidateKeys: (id: string) => {
      return [settingsQueryKeys.tax(), settingsQueryKeys.taxByProvider(id)];
    },
    setDataKey: (id: string) => settingsQueryKeys.taxByProvider(id),
  },
  delete: {
    endpoint: (id) => `/api/configurations/integrations/taxes/${id}`,
    invalidateKeys: (id: string) => {
      return [settingsQueryKeys.tax(), settingsQueryKeys.taxByProvider(id)];
    },
    setDataKey: (id: string) => settingsQueryKeys.taxByProvider(id),
  },
};

const dunningQueryKeys: ApiQueryItem = {
  list: {
    endpoint: (args) => `/api/dunning/dunningProcess`,
    queryKey: settingsQueryKeys.dunningList(),
  },
  create: {
    endpoint: () => `/api/dunning/dunningProcess`,
    invalidateKeys: [settingsQueryKeys.dunningList()],
    setDataKey: (id: string, args) => settingsQueryKeys.dunningDetail(id),
  },
  byId: {
    endpoint: (id: string) => `/api/dunning/dunningProcess/${id}`,
    queryKey: (id: string) => settingsQueryKeys.dunningDetail(id),
  },
  update: {
    endpoint: (id) => `/api/dunning/dunningProcess/${id}`,
    invalidateKeys: (id: string) => {
      return [
        settingsQueryKeys.dunningList(),
        settingsQueryKeys.dunningDetail(id),
      ];
    },
    setDataKey: (id: string) => settingsQueryKeys.dunningDetail(id),
  },
  delete: {
    endpoint: (id) => `/api/dunning/dunningProcess/${id}`,
    invalidateKeys: (id: string) => {
      return [
        settingsQueryKeys.dunningList(),
        settingsQueryKeys.dunningDetail(id),
      ];
    },
    setDataKey: (id: string) => settingsQueryKeys.dunningDetail(id),
  },
};

export const SETTING_SERVICE_API = asQueryUtil({
  customFields: customFieldQueryKeys,
  tax: taxQueryKeys,
  dunnings: dunningQueryKeys,
});

// Dunning
export function useGetDunningProcesses<T = ApiListResponse<IDunnings>>({
  config = {},
  filters,
  options,
}: {
  config?: GetListApiConfig;
  filters?: GetListApiFilter;
  options?: Partial<UseQueryOptions<ApiListResponse<IDunnings>, unknown, T>>;
} = {}) {
  const params = composeGetQuery(config, filters);
  return useQuery({
    queryKey: [...settingsQueryKeys.dunningList(), params],
    queryFn: () =>
      apiGet<ApiListResponse<IDunnings>>('/api/dunning/dunningProcess', {
        params,
      }).then((res) => res.data),
    ...options,
  });
}

export function useGetDunningById(
  dunningProcessId: string | undefined,
  options: Partial<UseQueryOptions<IDunningCreateSchema>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.dunningDetail(dunningProcessId!)],
    queryFn: () =>
      apiGet<IDunningCreateSchema>(
        `/api/dunning/dunningProcess/${dunningProcessId}`,
      ).then((res) => res.data),
    ...options,
    enabled: !!dunningProcessId,
    refetchOnWindowFocus: false,
  });
}

// Teams API
export function useCreateTeam(
  options: Partial<
    UseMutationOptions<ITeamResponseSchema, unknown, ITeam>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<ITeamResponseSchema, unknown, ITeam>({
    mutationFn: (payload) => {
      return apiPost<ITeamResponseSchema>(`/api/teams`, payload).then(
        (res) => res.data,
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(
        [...settingsQueryKeys.teamDetail(data.id)],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.teamHistory(data.id)],
      });
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.teamList()],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useUpdateTeam(
  { teamId }: { teamId: string | undefined },
  options: Partial<
    UseMutationOptions<ITeamResponseSchema, unknown, ITeamUpdateSchema>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<ITeamResponseSchema, unknown, ITeamUpdateSchema>({
    mutationFn: (payload) => {
      return apiPut<ITeamResponseSchema>(`/api/teams/${teamId}`, payload).then(
        (res) => res.data,
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(
        [...settingsQueryKeys.teamDetail(data.id)],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.teamHistory(data.id)],
      });
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.teamList()],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useChangeTeamOwner(
  options: Partial<
    UseMutationOptions<ITeamResponseSchema, unknown, ITeamOwnerUpdateSchema>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<ITeamResponseSchema, unknown, ITeamOwnerUpdateSchema>({
    mutationFn: (paylod) => {
      return apiPost<ITeamResponseSchema>(
        `/api/teams/${paylod?.id}/changeOwner/${paylod.userId}`,
      ).then((res) => res.data);
    },
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(
        [...settingsQueryKeys.teamDetail(data.id)],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.teamHistory(data.id)],
      });
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.teamList()],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function doGetTeamById(
  teamId: string | undefined,
): Promise<ITeamResponseSchema> {
  return apiGet<ITeamResponseSchema>(`/api/teams/${teamId}`).then(
    (res) => res.data,
  );
}

export function useGetTeamsById<SelectData = ITeamResponseSchema>(
  teamId: string | undefined,
  options: Partial<
    UseQueryOptions<ITeamResponseSchema, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.teamDetail(teamId!)],
    queryFn: () => doGetTeamById(teamId),
    ...options,
    enabled: !!teamId,
    refetchOnWindowFocus: false,
  });
}

export function useGetTeams<SelectData = ApiListResponse<ITeamResponseSchema>>(
  { config, filters }: { config: GetListApiConfig; filters?: GetListApiFilter },
  options: Partial<
    UseQueryOptions<ApiListResponse<ITeamResponseSchema>, unknown, SelectData>
  > = {},
) {
  const params = composeGetQuery(config, filters);
  return useQuery({
    queryKey: [...settingsQueryKeys.teamList(), params],
    queryFn: () =>
      apiGet<ApiListResponse<ITeamResponseSchema>>('/api/teams', {
        params,
      }).then((res) => res.data),
    ...options,
  });
}

/** @deprecated doGetTeams, instead use useGetTeams*/
export const doGetTeams = async (
  config: GetListApiConfig,
  filters?: GetListApiFilter,
): Promise<ApiListResponse<ITeam>> => {
  const url = `/api/teams`;

  const params = composeGetQuery(config, filters);
  const res = await apiGet<ApiListResponse<ITeam>>(url, {
    params,
  });
  return res.data;
};

export function useGetTeamHistory(
  {
    config,
    filters,
    teamId,
  }: { config: GetListApiConfig; filters?: GetListApiFilter; teamId: string },
  options: Partial<UseQueryOptions<ApiListResponse<IHistory>>> = {},
) {
  const params = composeGetQuery(config, filters);
  return useQuery({
    queryKey: [...settingsQueryKeys.teamHistory(teamId), params],
    queryFn: () =>
      apiGet<ApiListResponse<IHistory>>(`/api/teams/${teamId}/history`, {
        params,
      }).then((res) => res.data),
    ...options,
    enabled: !!teamId,
  });
}

export function useDeleteTeam(
  options: Partial<
    UseMutationOptions<ITeamResponseSchema, unknown, string>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<ITeamResponseSchema, unknown, string>({
    mutationFn: (teamId) => {
      return apiDelete<ITeamResponseSchema>(`/api/teams/${teamId}`).then(
        (res) => res.data,
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.team()],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}
// End  of Teams api

export function useGetTenantApiKeys({
  config,
  filters,
  options = {},
}: {
  config: GetListApiConfig;
  filters?: GetListApiFilter;
  options: Partial<UseQueryOptions<{ apiKeys: IApiKey[] }>>;
}) {
  const params = composeGetQuery(config, filters);
  return useQuery({
    queryKey: [...settingsQueryKeys.apiKeyList()],
    queryFn: () =>
      apiGet<{ apiKeys: IApiKey[] }>('/api/configurations/tenantApiKeys', {
        params,
      }).then((res) => res.data),
    ...options,
  });
}

export function useAddTenantApiKey(
  options: Partial<UseMutationOptions<{ apiKey: string }, unknown, any>> = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<{ apiKey: string }, unknown, any>({
    mutationFn: (payload) => {
      return apiPost<{ apiKey: string }>(
        `/api/configurations/tenantApiKeys`,
        payload,
      ).then((res) => res.data);
    },

    onSuccess: (...data) => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.apiKeyList()],
      });
      onSuccess && onSuccess(...data);
    },
    ...restOptions,
  });
}

export function useRevokeTenantApiKey(
  options: Partial<UseMutationOptions<void, unknown, string>> = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<void, unknown, string>({
    mutationFn: (apiKeyId) => {
      return apiPost(
        `/api/configurations/tenantApiKeys/${apiKeyId}/revoke`,
      ).then((res) => res.data);
    },

    onSuccess: (...data) => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.apiKeyList()],
      });
      onSuccess && onSuccess(...data);
    },
    ...restOptions,
  });
}

export function useGetConfigurationPricing(
  options: Partial<UseQueryOptions<IBillPricing>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.pricing()],
    queryFn: () =>
      apiGet<IBillPricing>('/api/configurations/billing/pricing').then(
        (res) => res.data,
      ),
    ...options,
  });
}

export function useUpdateConfigurationPricing(
  options: Partial<
    UseMutationOptions<IBillPricing, unknown, IBillPricing>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<IBillPricing, unknown, IBillPricing>({
    mutationFn: (payload) => {
      return apiPut<IBillPricing>(
        `/api/configurations/billing/pricing`,
        payload,
      ).then((res) => res.data);
    },

    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(settingsQueryKeys.pricing(), data);
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useGetConfigurationEsign(
  options: Partial<UseQueryOptions<ESignConfig>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.esignConfig()],
    queryFn: () =>
      apiGet<ESignConfig>(`/api/configurations/integrations/esign`).then(
        (res) => res.data,
      ),

    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export type EsignUpdateParams = {
  method: 'create' | 'update';
  data: ESignConfig;
};

export function useEsignMutation(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      EsignUpdateParams | { method: 'delete'; data?: never }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    unknown,
    EsignUpdateParams | { method: 'delete'; data?: never }
  >({
    mutationFn: async ({ method, data }) => {
      switch (method) {
        case 'create': {
          await apiPost(`/api/configurations/integrations/esign`, data);
          break;
        }
        case 'update': {
          await apiPut(`/api/configurations/integrations/esign`, data);
          break;
        }
        case 'delete': {
          await apiDelete(`/api/configurations?category=eSIGN`);
          break;
        }
        default:
          throw new Error(`Invalid method: ${method}`);
      }
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.esignConfig(),
      });
    },
    ...options,
  });
}

export async function doGetAdobeAcrobatSignOAuthUrl() {
  const res = await apiGet<{ url: string }>(
    '/oauth/adobe-acrobat-sign/authorize',
    {
      withCredentials: true,
    },
  );
  return res.data;
}

export function useGetCrmConfiguration<SelectData = CrmConnection>(
  options: Partial<UseQueryOptions<CrmConnection, unknown, SelectData>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.crm()],
    queryFn: async () =>
      apiGet<CrmConnection>(`connector/connections/crm`).then(
        (res) => res.data,
      ),
    ...options,
  });
}

export function useSaveHubSpotSyncBehavior(
  options: Partial<
    UseMutationOptions<
      unknown,
      unknown,
      HubSpotSyncBehavior,
      { previousCrmConnection?: CrmConnection }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onError, onSettled, ...restOptions } = options;
  return useMutation({
    mutationFn: async (syncBehavior: HubSpotSyncBehavior) => {
      const { data } = await apiPut(
        '/connector/crm/hubspot/syncBehavior',
        syncBehavior,
      );
      return data;
    },
    onMutate: async (syncBehavior: HubSpotSyncBehavior) => {
      // Cancel any outgoing refetches so they don't overwrite the optimistic update
      await queryClient.cancelQueries({ queryKey: settingsQueryKeys.crm() });

      const previousCrmConnection = queryClient.getQueryData<CrmConnection>(
        settingsQueryKeys.crm(),
      );

      // Optimistically flip the toggle so the UI responds before the request settles
      if (previousCrmConnection?.hubspot) {
        queryClient.setQueryData<CrmConnection>(settingsQueryKeys.crm(), {
          ...previousCrmConnection,
          hubspot: {
            ...previousCrmConnection.hubspot,
            syncBehavior: {
              ...previousCrmConnection.hubspot.syncBehavior,
              fromMn: {
                ...previousCrmConnection.hubspot.syncBehavior?.fromMn,
                ...syncBehavior.fromMn,
              },
            },
          },
        });
      }

      return { previousCrmConnection };
    },
    onError: (err, variables, ctx) => {
      if (ctx?.previousCrmConnection) {
        queryClient.setQueryData(
          settingsQueryKeys.crm(),
          ctx.previousCrmConnection,
        );
      }
      onError?.(err, variables, ctx);
    },
    onSettled: (data, err, variables, ctx) => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.crm(),
      });
      onSettled?.(data, err, variables, ctx);
    },
    ...restOptions,
  });
}

export function useHasCrmConfigured(
  options: Partial<UseQueryOptions<CrmConnection, unknown, unknown>> = {},
) {
  return useGetCrmConfiguration({
    ...options,
    select: (data) => {
      const defaultResponse = {
        crmConnected: false,
        systemLabel: null,
        system: null,
      };
      if (!data.attio && !data.hubspot && !data.salesforce) {
        return defaultResponse;
      }
      if (data.attio) {
        return {
          crmConnected: true,
          systemLabel: 'Attio',
          system: ExternalSystem.ATTIO,
        };
      }
      if (data.hubspot) {
        return {
          crmConnected: true,
          systemLabel: 'Hubspot',
          system: ExternalSystem.HUBSPOT,
        };
      }
      if (data.salesforce) {
        return {
          crmConnected: true,
          systemLabel: 'Salesforce',
          system: ExternalSystem.SALESFORCE,
        };
      }
      logger.error(`Invalid CRM value, expected Attio, Hubspot, or Salesforce`);
      return defaultResponse;
    },
  });
}

export async function doGetCrmAuthorizeRedirectUrl(
  type: CrmType,
  customUrl?: string | null | undefined,
) {
  const params = customUrl ? { customUrl } : {};
  const res = await apiGet<{ url: string }>(
    `connector/auth/crm/${type}/authorize`,
    { params },
  );
  return res.data;
}

export function useResyncSalesforceProductCatalog(
  options: Partial<UseMutationOptions<CrmResyncResponse, unknown, void>> = {},
) {
  return useMutation({
    mutationFn: () =>
      apiPost<CrmResyncResponse>('/connector/crm/salesforce/sync', {
        type: 'catalog',
      }).then((res) => res.data),
    ...options,
  });
}

export function useDeleteHubspotCrmConnection(
  options: Partial<UseMutationOptions<void, unknown, void>> = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<void, unknown, void>({
    mutationFn: () =>
      apiDelete('/connector/crm/hubspot/connection').then(() => undefined),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [...settingsQueryKeys.crm()] });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
}

export function useGetAttioDealStages<SelectData = AttioDealStage[]>(
  options: Partial<UseQueryOptions<AttioDealStage[], unknown, SelectData>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.crm(), 'attio', 'dealStages'],
    queryFn: () =>
      apiGet<AttioDealStage[]>('connector/crm/attio/deals/stages').then(
        (res) => res.data,
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useSaveQuoteStatusToDealStageMappings(
  options: Partial<
    UseMutationOptions<
      AttioConnection,
      unknown,
      QuoteStatusToDealStageMappingType[]
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<
    AttioConnection,
    unknown,
    QuoteStatusToDealStageMappingType[]
  >({
    mutationFn: (payload) =>
      apiPost<AttioConnection>(
        'connector/crm/attio/deals/stages/mapping',
        payload,
      ).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.crm()],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useGetAccountingConnections<
  SelectData = AccountingConnectionsResponse,
>(
  options: Partial<
    UseQueryOptions<AccountingConnectionsResponse, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.accounting()],
    queryFn: () =>
      apiGet<AccountingConnectionsResponse>(
        `/connector/connections/accounting`,
      ).then((res) => res.data),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useHasAccountingConfigured<
  SelectData = {
    accountingConnected: boolean;
    systems: { system: ExternalSystem; label: string }[];
  },
>(
  options: Partial<
    UseQueryOptions<AccountingConnectionsResponse, unknown, SelectData>
  > = {},
) {
  return useGetAccountingConnections({
    ...options,
    select: (data) => {
      const systems: { system: ExternalSystem; label: string }[] = [];
      if (data?.quickbooks) {
        systems.push({
          system: ExternalSystem.QUICKBOOKS,
          label: 'QuickBooks',
        });
      }
      if (data?.netsuite) {
        systems.push({ system: ExternalSystem.NETSUITE, label: 'NetSuite' });
      }
      if (data?.xero) {
        systems.push({ system: ExternalSystem.XERO, label: 'Xero' });
      }
      if (data?.rillet) {
        systems.push({ system: ExternalSystem.RILLET, label: 'Rillet' });
      }
      return {
        accountingConnected: systems.length > 0,
        systems,
      };
    },
  });
}

export async function doGetAccountingAuthorizeRedirectUrl(
  payload: AccountingRedirectPayload,
) {
  let url = `connector/auth/accounting/${payload.type}/authorize`;
  if (payload.type === 'quickbooks') {
    url += `?environment=${payload.env.toLowerCase()}`;
  }
  const res = await apiGet<{ url: string }>(url);
  return res.data;
}

export function useGetIntegrationLogInfiniteLoad(
  query: TIntegrationLogRequestSchema,
  options: Partial<
    Partial<
      UndefinedInitialDataInfiniteOptions<
        ApiListInfiniteScrollResponse<IntegrationLogResponse>
      >
    >
  > = {},
) {
  return useInfiniteQuery<
    ApiListInfiniteScrollResponse<IntegrationLogResponse>
  >({
    // Spread, not nested — a nested key array would not prefix-match
    // `settingsQueryKeys.integrationLog()` on invalidation.
    queryKey: [...settingsQueryKeys.integrationLog(), { ...query }],
    queryFn: ({ pageParam = undefined }) =>
      apiGet<ApiListInfiniteScrollResponse<IntegrationLogResponse>>(
        `/connector/monitoring/sync-logs`,
        {
          params: {
            ...query,
            next: pageParam,
          },
        },
      ).then((res) => res.data),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (!lastPage.content.length || !lastPage.next) {
        return undefined;
      }

      return lastPage.next;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0,
    ...options,
  });
}

export function useGetIntegrationLogById(
  id: string | undefined,
  options: Partial<UseQueryOptions<IntegrationLogResponse>> = {},
) {
  return useQuery<IntegrationLogResponse>({
    queryKey: settingsQueryKeys.integrationLogDetail(id ?? ''),
    queryFn: () =>
      apiGet<IntegrationLogResponse>(
        `connector/monitoring/sync-logs/${id}`,
      ).then((res) => res.data),
    ...options,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
}

// Sync Log Publish (Re-sync)
export function useSyncLogPublish(
  options: Partial<
    UseMutationOptions<
      IntegrationLogEventPublishResponse,
      unknown,
      IntegrationLogEventPublishReq
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<
    IntegrationLogEventPublishResponse,
    unknown,
    IntegrationLogEventPublishReq
  >({
    mutationFn: async (payload) => {
      await apiPost(`/api/tenants/publish`, payload);

      // If we reach here, the request was successful
      // This is a special case where backend does not return any data HTTP status 204
      return {
        success: true,
        message:
          'Re-sync initiated successfully! It may take up to a few minutes to complete.',
      };
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.integrationLog(),
      });
      // Record-level integration popovers embed the same sync logs.
      queryClient.invalidateQueries({
        queryKey: associationsQueryKeys.base,
      });
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}

// Slack
export async function doGetSlackAuthorizeRedirectUrl() {
  const res = await apiGet<{ url: string }>('/oauth/slack/authorize', {
    withCredentials: true,
  });
  return res.data;
}

export function useGetSlackConnection<SelectData = ISlackConnectionResponse>(
  options: Partial<
    UseQueryOptions<ISlackConnectionResponse, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.slack()],
    queryFn: () =>
      apiGet<ISlackConnectionResponse>(`/slack/connection`).then(
        (res) => res.data,
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useDeleteSlackConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiDelete('/slack/connection'),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.slack()],
      });
    },
  });
}

export async function doGetSlackChannels() {
  const res = await apiGet<ISlackChannelResponse>('/slack/connection/channels');
  return res.data;
}

export function useChangeSlackConnectionStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (method: 'ENABLE' | 'DISABLE') =>
      apiPost(`/slack/connection/${method.toLowerCase()}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.slack()],
      });
    },
  });
}

// notifications
export function useGetNotificationSettings(
  options: Partial<UseQueryOptions<INotificationSchema>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.notifications()],
    queryFn: () =>
      apiGet<INotificationSchema>(`/api/configurations/notifications`).then(
        (res) => res.data,
      ),
    ...options,
  });
}

export function useGetCrmHealthCheck(
  options: Partial<UseQueryOptions<CrmHealthCheck>> = {},
) {
  return useQuery({
    queryKey: settingsQueryKeys.crmHealthCheck(),
    queryFn: () =>
      apiGet<CrmHealthCheck>(`connector/connections/crm/healthcheck`).then(
        (res) => res.data,
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

// api/configurations/notifications
export function useConfigureNotification(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      INotificationSchema,
      { previousNotifications?: unknown }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => {
      return apiPost<void>('/api/configurations/notifications', payload).then(
        (res) => res.data,
      );
    },

    onMutate: async (payload: INotificationSchema) => {
      await queryClient.cancelQueries({
        queryKey: [...settingsQueryKeys.notifications()],
      });

      const previousNotifications = queryClient.getQueryData([
        ...settingsQueryKeys.notifications(),
      ]);
      queryClient.setQueryData(settingsQueryKeys.notifications(), payload);

      return { previousNotifications, payload };
    },
    onError: (err, variables, context) => {
      onError?.(err, variables, context);
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          settingsQueryKeys.notifications(),
          context.previousNotifications,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.notifications()],
      });
    },
    ...restOptions,
  });
}

export function useConfigNotificationEmailOrDomain(
  options: Partial<
    UseMutationOptions<
      INotificationItemSchema,
      unknown,
      INotificationItemSchema
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<INotificationItemSchema, unknown, INotificationItemSchema>(
    {
      mutationFn: (payload) => {
        return apiPost<INotificationItemSchema>(
          `/api/configurations/notifications/types/${payload.type}`,
          payload.emailOrDomains,
        ).then((res) => res.data);
      },
      onSuccess: (res, payload, context) => {
        const notificationConfig: INotificationSchema | undefined =
          queryClient.getQueryData([...settingsQueryKeys.notifications()]);

        const isTypeExists = notificationConfig?.notifications.some(
          (item) => item.type === payload.type,
        );

        const updatedNotifications = notificationConfig?.notifications.map(
          (item) => {
            if (item.type === payload.type) {
              if (res) {
                return res;
              }
              return payload;
            }
            return item;
          },
        );
        if (res && !isTypeExists) {
          updatedNotifications?.push(res);
        }

        queryClient.setQueryData([...settingsQueryKeys.notifications()], {
          ...notificationConfig,
          notifications: updatedNotifications,
        });
        onSuccess && onSuccess(res, payload, context);
      },
      ...restOptions,
    },
  );
}

// Quote Email Reminders

export function useGetQuoteExpirationReminder() {
  return useQuery({
    queryKey: [...settingsQueryKeys.quoteExpirationReminder()],
    queryFn: () =>
      apiGet<QuoteExpirationReminderSchema>(
        `/api/configurations/notifications/quoteEmail`,
      ).then((res) => res.data),
  });
}

export const useSaveQuoteExpirationReminder = (
  options: Partial<
    UseMutationOptions<void, unknown, QuoteExpirationReminderSchema>
  > = {},
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<void, unknown, QuoteExpirationReminderSchema>({
    mutationFn: (payload) => {
      return apiPost<void>(
        `/api/configurations/notifications/quoteEmail`,
        payload,
      ).then((res) => res.data);
    },

    onSuccess: (res, payload, context) => {
      queryClient.setQueryData(
        [...settingsQueryKeys.quoteExpirationReminder()],
        payload,
      );
      onSuccess && onSuccess(res, payload, context);
    },
    ...restOptions,
  });
};

export const useGetCurrencySettings = <
  SelectData = ApiListResponse<ICurrencyResp>,
>(
  {
    config,
    filters,
  }: { config?: GetListApiConfig; filters?: GetListApiFilter } = {},
  options: Partial<
    UseQueryOptions<ApiListResponse<ICurrencyResp>, unknown, SelectData>
  > = {
    refetchOnWindowFocus: false,
  },
) => {
  config = config || {
    first: 0,
    rows: 1000,
    page: 0,
    sortField: 'defaultCurrency:desc,code:asc',
    sortOrder: null, // use sort orders specified in sortField
  };
  const params = composeGetQuery(config, filters);
  return useQuery({
    queryKey: [...settingsQueryKeys.currencyList(), params],
    queryFn: () =>
      apiGet<ApiListResponse<ICurrencyResp>>(`/api/currencies`, {
        params,
      }).then((res) => res.data),
    ...options,
  });
};

export const useCreateOrUpdateCurrencySettings = (
  options: Partial<
    UseMutationOptions<
      ICurrencyResp,
      unknown,
      | { action: 'create'; payload: ICurrencySchema }
      | { action: 'update'; payload: ICurrencyResp }
    >
  > & { skipInvalidation?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  const { onSuccess, skipInvalidation, ...restOptions } = options;
  return useMutation<
    ICurrencyResp,
    unknown,
    | { action: 'create'; payload: ICurrencySchema }
    | { action: 'update'; payload: ICurrencyResp }
  >({
    mutationFn: ({ action, payload }) => {
      if (action === 'create') {
        return apiPost<ICurrencyResp>(`/api/currencies`, payload).then(
          (res) => res.data,
        );
      } else {
        return apiPut<ICurrencyResp>(
          `/api/currencies/${payload.id}`,
          payload,
        ).then((res) => res.data);
      }
    },
    onSuccess: (data, variables, context) => {
      if (!skipInvalidation) {
        if (variables.action === 'create') {
          updateListCacheWithAddedItem(
            queryClient,
            [...settingsQueryKeys.currencyList()],
            data,
          );
        } else {
          updateListCacheWithUpdatedItem(
            queryClient,
            [...settingsQueryKeys.currencyList()],
            data,
          );
        }
        // The backend clears the previous default; mirror that so the cache never holds two
        if (data.defaultCurrency) {
          queryClient.setQueriesData<ApiListResponse<ICurrencyResp>>(
            { queryKey: [...settingsQueryKeys.currencyList()] },
            (existing) =>
              existing && {
                ...existing,
                content: existing.content.map((currency) =>
                  currency.id === data.id
                    ? currency
                    : { ...currency, defaultCurrency: false },
                ),
              },
          );
        }
        queryClient.invalidateQueries({
          queryKey: [...settingsQueryKeys.currency()],
        });
      }
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteCurrencySettings = (
  options: Partial<UseMutationOptions<void, unknown, { id: string }>> = {},
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<void, unknown, { id: string }>({
    mutationFn: ({ id }) =>
      apiDelete<void>(`/api/currencies/${id}`).then((res) => res.data),

    onSuccess: (data, variables, context) => {
      updateListCacheWithRemovedItem(
        queryClient,
        [...settingsQueryKeys.currencyList()],
        variables.id,
      );
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.currency()],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
};

export const doGetPaymentGatewaysForTenant = async (
  signal: AbortSignal,
  tenantId: string,
  config: GetListApiConfig = {},
  filters?: GetListApiFilter,
): Promise<ApiListResponse<IPaymentGateway>> => {
  const url = `/api/paymentGateways`;
  const params = composeGetQuery(config, filters);
  const res = await apiGet<ApiListResponse<IPaymentGateway>>(url, {
    params,
    signal,
  });
  return res.data;
};

// Quote Settings

export function useGetQuoteSettings<SelectData = IQuoteSettingsConfigSchema>(
  options: Partial<
    UseQueryOptions<IQuoteSettingsConfigSchema, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.quoteSettings()],
    queryFn: () =>
      apiGet<IQuoteSettingsConfigSchema>(
        `/api/configurations/quoteSettings`,
      ).then((res) => res.data),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useSaveQuoteSettings(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      IQuoteSettingsConfigSchema,
      { previousPayload: unknown }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...restOptions } = options;
  return useMutation({
    mutationFn: (payload) => {
      return apiPost<void>('/api/configurations/quoteSettings', payload).then(
        (res) => res.data,
      );
    },
    onMutate: async (payload: IQuoteSettingsConfigSchema) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: settingsQueryKeys.quoteSettings(),
      });

      // Snapshot the previous value
      const previousPayload = queryClient.getQueryData(
        settingsQueryKeys.quoteSettings(),
      );

      // Optimistically update to the new value
      queryClient.setQueryData(settingsQueryKeys.quoteSettings(), payload);

      // Return a context with the previous and new payload
      return { previousPayload, payload };
    },
    onError: (err, variables, context) => {
      onError?.(err, variables, context);

      if (context?.previousPayload) {
        queryClient.setQueryData(
          settingsQueryKeys.quoteSettings(),
          context.previousPayload,
        );
      }
    },
    onSettled: (_) => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.quoteSettings(),
      });
    },
    ...restOptions,
  });
}

// quote Template Config
export function useGetConfigQuoteTemplate(
  options: Partial<UseQueryOptions<IQuoteTemplateConfigRes>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.quoteTemplate()],
    queryFn: () =>
      apiGet<IQuoteTemplateConfigRes>(
        `/api/configurations/quoteTemplates`,
      ).then((res) => res.data),
    ...options,
  });
}

export function useConfigQuoteTemplate(
  options: Partial<
    UseMutationOptions<
      IQuoteTemplateConfigRes,
      unknown,
      IQuoteTemplateConfigRes
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<IQuoteTemplateConfigRes, unknown, IQuoteTemplateConfigRes>(
    {
      mutationFn: (payload) =>
        apiPut<IQuoteTemplateConfigRes>(`/api/configurations/quoteTemplates`, {
          defaultTemplate: 'QUOTE_DEFAULT_1', // FALLBACK TO DEFAULT TEMPLATE UNTIL BE HAS THIS DATA
          ...payload,
        }).then((res) => res.data),
      onSuccess: (data, variables, context) => {
        queryClient.setQueryData([...settingsQueryKeys.quoteTemplate()], data);
        onSuccess && onSuccess(data, variables, context);
      },
      ...restOptions,
    },
  );
}

export function useConfigurationSalesApprovalMessage(
  options: Partial<UseQueryOptions<IQuoteApprovalMessageSchema>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.salesQuoteApprovalMessage()],
    queryFn: () =>
      apiGet<IQuoteApprovalMessageSchema>(
        `/api/configurations/sales/quoteApprovalMessage`,
      ).then((res) => res.data),

    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useGetInvoiceSettings(
  options: Partial<UseQueryOptions<IInvoiceSettings>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.invoiceSettings()],
    queryFn: () =>
      apiGet<IInvoiceSettings>(`/api/configurations/invoiceSettings`).then(
        (res) => res.data,
      ),
    ...options,
  });
}

export function useUpdateInvoiceSettings(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      IInvoiceSettings,
      { previousPayload: unknown }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => {
      return apiPut<void>(`/api/configurations/invoiceSettings`, payload).then(
        (res) => res.data,
      );
    },
    onMutate: async (payload: IInvoiceSettings) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: settingsQueryKeys.invoiceSettings(),
      });

      // Snapshot the previous value
      const previousPayload = queryClient.getQueryData(
        settingsQueryKeys.invoiceSettings(),
      );

      // Optimistically update to the new value
      queryClient.setQueryData(settingsQueryKeys.invoiceSettings(), payload);

      // Return a context with the previous and new payload
      return { previousPayload, payload };
    },
    onError: (err, variables, context) => {
      onError?.(err, variables, context);

      if (context?.previousPayload) {
        queryClient.setQueryData(
          settingsQueryKeys.quoteSettings(),
          context.previousPayload,
        );
      }
    },
    onSettled: (_) => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.invoiceSettings(),
      });
    },
    ...restOptions,
  });
}

export function useGetEarliestManualPaymentDate(
  options: Partial<UseQueryOptions<{ earliestManualPaymentDate: string }>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.earliestManualPaymentDateSettings()],
    queryFn: () =>
      apiGet<{ earliestManualPaymentDate: string }>(
        `/api/configurations/payments/earliestManualPaymentDate`,
      ).then((res) => res.data),
    ...options,
  });
}

export function useUpdateEarliestManualPaymentDate(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      { earliestManualPaymentDate: string },
      { previousPayload: unknown }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => {
      return apiPut<void>(
        `/api/configurations/payments/earliestManualPaymentDate`,
        payload,
      ).then((res) => res.data);
    },
    onMutate: async (payload: any) => {
      await queryClient.cancelQueries({
        queryKey: settingsQueryKeys.earliestManualPaymentDateSettings(),
      });

      const previousPayload = queryClient.getQueryData(
        settingsQueryKeys.earliestManualPaymentDateSettings(),
      );

      queryClient.setQueryData(
        settingsQueryKeys.earliestManualPaymentDateSettings(),
        payload,
      );

      return { previousPayload, payload };
    },
    onError: (err, variables, context) => {
      onError?.(err, variables, context);

      if (context?.previousPayload) {
        queryClient.setQueryData(
          settingsQueryKeys.earliestManualPaymentDateSettings(),
          context.previousPayload,
        );
      }
    },
    onSettled: (_) => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.earliestManualPaymentDateSettings(),
      });
    },
    ...restOptions,
  });
}

// default payment collect automatically config
export function useGetPaymentCollectAutoConfig(
  options: Partial<UseQueryOptions<IPaymentCollectAutoConfigResSchema>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.billGroupConfig()],
    queryFn: () =>
      apiGet<IPaymentCollectAutoConfigResSchema>(
        `/api/configurations/billGroups`,
      ).then((res) => res.data),
    ...options,
  });
}

export function useConfigurePaymentCollectAutoConfig(
  options: Partial<
    UseMutationOptions<
      GenericApiResponse,
      unknown,
      IPaymentCollectAutoConfigReqSchema
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<
    GenericApiResponse,
    unknown,
    IPaymentCollectAutoConfigReqSchema
  >({
    mutationFn: (payload) => {
      return apiPost<GenericApiResponse>(
        `/api/configurations/billGroups`,
        payload,
      ).then((res) => res.data);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.billGroupConfig()],
      });
    },
    ...options,
  });
}

export const useDefaultDocumentUploader = (
  options: Partial<
    UseMutationOptions<GenericApiResponse, unknown, FormData>
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation<GenericApiResponse, unknown, FormData>({
    mutationFn: async (payload) => {
      const res = await apiUpload(
        '/api/configurations/quotes/documents/upload',
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.quoteSettings()],
      });
    },
    ...options,
  });
};

export const useDefaultDocumentRemover = (
  options: Partial<
    UseMutationOptions<GenericApiResponse, unknown, string>
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation<GenericApiResponse, unknown, string>({
    mutationFn: async (payload) => {
      const res = await apiDelete(
        `/api/configurations/quotes/documents/${payload}`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.quoteSettings()],
      });
    },
    ...options,
  });
};

/** Tax Configuration APIs */
export async function doTestTaxConnection(data: ITaxConnectionVerifyReqSchema) {
  const res = await apiPost(
    `/api/configurations/integrations/taxes/test`,
    data,
  );
  return res.data;
}

export function useDealRoomConfig(
  options: Partial<UseQueryOptions<IDealRoomSettingSchema>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.dealroomConfig()],
    queryFn: () =>
      apiGet<IDealRoomSettingSchema>(`/api/configurations/dealRoom`).then(
        (res) => res.data,
      ),
    ...options,
  });
}

export function useSaveDealRoomConfig(
  options: Partial<
    UseMutationOptions<GenericApiResponse, unknown, IDealRoomSettingSchema>
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<GenericApiResponse, unknown, IDealRoomSettingSchema>({
    mutationFn: (payload) => {
      return apiPost<GenericApiResponse>(
        `/api/configurations/dealRoom`,
        payload,
      ).then((res) => res.data);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.dealroomConfig()],
      });
    },
    ...options,
  });
}

export function useReferenceResources(
  {
    filters,
  }: {
    filters?: GetListApiFilter;
  } = {},
  options: Partial<
    UseQueryOptions<unknown, unknown, ApiListResponse<ReferenceResource>>
  > = {},
) {
  const config: GetListApiConfig = {
    ...DEFAULT_PAGER,
    rows: 100,
  };
  const params = composeGetQuery(config, filters);

  return useQuery<unknown, unknown, ApiListResponse<ReferenceResource>>({
    queryKey: settingsQueryKeys.referenceResources(),
    queryFn: () =>
      apiGet<ApiListResponse<ReferenceResource>>(`/api/referenceResources`, {
        params,
      }).then((res) => res.data),
    ...options,
  });
}
export async function doGetReferenceResourceById(referenceId: string) {
  const response = await apiGet<ReferenceResource>(
    `/api/referenceResources/${referenceId}?attachContent=true`,
  );
  return response.data;
}

export function useSaveReferenceResource(
  options: Partial<
    UseMutationOptions<
      GenericApiResponse,
      unknown,
      ReferenceResource & { file: File | null }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<
    GenericApiResponse,
    unknown,
    ReferenceResource & { file: File | null }
  >({
    mutationFn: (payload) => {
      const formData = new FormData();
      const jsonBlob = new Blob(
        [
          JSON.stringify({
            name: payload.name,
            description: payload.description,
            category: payload.category,
            resourceType: payload.resourceType,
          }),
        ],
        {
          type: 'application/json',
        },
      );
      formData.append('data', jsonBlob, 'blob');
      if (payload.file) {
        formData.append('file', payload.file);
      }
      return apiUpload<GenericApiResponse>(
        `/api/referenceResources`,
        formData,
      ).then((res) => res.data);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.referenceResources()],
      });
    },
    ...options,
  });
}

export function useUpdateReferenceResource(
  options: Partial<
    UseMutationOptions<
      ReferenceResource,
      unknown,
      ReferenceResource & { file: File | null }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<
    ReferenceResource,
    unknown,
    ReferenceResource & { file: File | null }
  >({
    mutationFn: (payload) => {
      const formData = new FormData();
      const jsonBlob = new Blob(
        [
          JSON.stringify({
            name: payload.name,
            description: payload.description,
            category: payload.category,
            resourceType: payload.resourceType,
          }),
        ],
        {
          type: 'application/json',
        },
      );
      formData.append('data', jsonBlob, 'blob');
      if (payload.file) {
        formData.append('file', payload.file);
      }
      return apiUploadPut<ReferenceResource>(
        `/api/referenceResources/${payload.id}`,
        formData,
      ).then((res) => res.data);
    },

    onSuccess: (data, variables, context) => {
      queryClient.refetchQueries({
        queryKey: settingsQueryKeys.referenceResourceById(data.id),
      });
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.referenceResources()],
      });

      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export const useReferenceResource = (
  options: Partial<UseMutationOptions<void, unknown, { id: string }>> = {},
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<void, unknown, { id: string }>({
    mutationFn: ({ id }) =>
      apiDelete<void>(`/api/referenceResources/${id}`).then((res) => res.data),

    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [...settingsQueryKeys.referenceResources()],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
};

export function useGetReferenceResourceByReferenceId(
  referenceId: string,
  options: Partial<UseQueryOptions<ReferenceResource>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.referenceResourceById(referenceId)],
    queryFn: () =>
      apiGet<ReferenceResource>(
        `/api/referenceResources/${referenceId}?attachContent=true`,
      ).then((res) => res.data),
    enabled: !!referenceId,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useGetAuditLogs(
  {
    year,
    month,
    day,
  }: {
    year: number | string;
    month: number | string;
    day: number | string;
  },
  options: Partial<UseQueryOptions<AuditLog[]>> = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.auditLog(), year, month, day],
    enabled: Boolean(year && month && day),
    queryFn: () =>
      apiGet<AuditLog[]>(`/api/log/${year}/${month}/${day}`).then(
        (res) => res.data,
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useDownloadAllAuditLogs(
  options: Partial<
    UseMutationOptions<
      { data: ArrayBuffer; filename: string },
      unknown,
      { year: number; month: number; day: number }
    >
  > = {},
) {
  return useMutation({
    mutationFn: async ({ year, month, day }) => {
      const response = await apiGet(`/api/log/zip/${year}/${month}/${day}`, {
        responseType: 'arraybuffer',
      });

      return {
        data: response.data,
        filename: `audit-logs-${year}-${month}-${day}.csv`,
      };
    },
    ...options,
  });
}

export function useGetQuoteDocumentTypes<SelectData = QuoteDocumentType[]>(
  options: Partial<
    UseQueryOptions<QuoteDocumentType[], unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...settingsQueryKeys.quoteDocumentTypes()],
    queryFn: () =>
      apiGet<QuoteDocumentType[]>(
        `/api/configurations/selectOptions/QUOTE_DOCUMENT_TYPE`,
      ).then((res) => res.data),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useCreateQuoteDocumentType(
  options: Partial<
    UseMutationOptions<QuoteDocumentType, unknown, QuoteDocumentTypeRequest>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<QuoteDocumentType, unknown, QuoteDocumentTypeRequest>({
    mutationFn: (payload) =>
      apiPost<QuoteDocumentType>(
        `/api/configurations/selectOptions/QUOTE_DOCUMENT_TYPE`,
        payload,
      ).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.quoteDocumentTypes(),
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useUpdateQuoteDocumentType(
  options: Partial<
    UseMutationOptions<
      QuoteDocumentType,
      unknown,
      { id: string; data: QuoteDocumentTypeRequest }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<
    QuoteDocumentType,
    unknown,
    { id: string; data: QuoteDocumentTypeRequest }
  >({
    mutationFn: ({ id, data }) =>
      apiPut<QuoteDocumentType>(
        `/api/configurations/selectOptions/QUOTE_DOCUMENT_TYPE/${id}`,
        data,
      ).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.quoteDocumentTypes(),
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useDeleteQuoteDocumentType(
  options: Partial<UseMutationOptions<void, unknown, string>> = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<void, unknown, string>({
    mutationFn: (id) =>
      apiPut<void>(`/api/configurations/selectOptions/${id}/delete`).then(
        (res) => res.data,
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.quoteDocumentTypes(),
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export const useGetTenantQuoteDocumentDefinitions = () => {
  return useQuery({
    queryKey: settingsQueryKeys.quoteDocumentDefinitions(),
    queryFn: async (): Promise<DocumentDefinition> => {
      const res = await apiGet(`/api/documentDefinitions/quotes`);
      return res.data;
    },
  });
};

export const useUploadTenantQuoteDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await apiUpload(`/api/documentDefinitions/quotes`, formData);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: settingsQueryKeys.quoteDocumentDefinitions(),
      });
    },
  });
};
